import type { MenuItem } from '@/api'
import type { Exhibitor } from '@/model'
import { useFairStore } from '@/stores/fair'

export function isApprovedExhibitor(exhibitor: Exhibitor) {
  return !exhibitor.isEmpty() && exhibitor.status === 'CompleteApproval'
}

// meta.label은 문서 용도로 사용
// 메뉴명을 변경하려면 DB 내 'menu_items' 테이블의 'label' 컬럼을 수정할 것
export const menuRoutes = [
  {
    path: '/',
    name: 'root-fair_list',
    component: () => import('@/views/pages/FairList.vue'),
    meta: {
      label: '연간 일정'
    }
  },
  {
    path: '/exhibitor/:FAIR_ID/register',
    name: 'exhibition-application-submit',
    component: () => import('@/views/pages/exhibition/Application.vue'),
    meta: {
      label: '참가기업 접수 신청',
      canDisplay: (exhibitor: Exhibitor) => {
        const fairStore = useFairStore()

        return exhibitor.isEmpty() && fairStore.isExhibitorApplicablePeriod
      }
    }
  },
  {
    path: '/exhibitor/:FAIR_ID/application',
    name: 'exhibition-application-check',
    component: () => import('@/views/pages/exhibition/RegistrationConfirm.vue'),
    meta: {
      label: '참가기업 접수 내역 확인',
      canDisplay: (exhibitor: Exhibitor) => {
        return !exhibitor.isEmpty() && ['CompleteApproval', 'WaitApproval'].includes(exhibitor.status ?? '')
      },
      isRequireExhibitorId: true
    }
  },
  {
    path: '/exhibitor/:FAIR_ID/receipt',
    name: 'exhibition-receipt',
    component: () => import('@/views/pages/exhibition/Receipt.vue'),
    meta: { label: '출품료 납부내역 확인', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/facilities',
    name: 'support-request_facility',
    component: () => import('@/views/pages/support/Facilities.vue'),
    meta: { label: '부대시설 신청 및 내역', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/onsite-staffs',
    name: 'support-onsite_staffs',
    component: () => import('@/views/pages/support/OnsiteStaffs.vue'),
    meta: { label: '참가기업 출입증 신청', isRequireExhibitorId: true, isRequireApproval: false }
  },
  {
    path: '/support/:FAIR_ID/brands',
    name: 'support-gdpp_brand',
    component: () => import('@/views/pages/support/GdppBrand.vue'),
    meta: { label: '업체 소개 양식 작성', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/linkonbiz',
    name: 'support-linkonbiz',
    component: () => import('@/views/pages/support/Linkonbiz.vue'),
    meta: { label: '온라인 홍보 신청 및 제품 등록 (링크온비즈 바로가기)', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/support/:FAIR_ID/onsite-events',
    name: 'support-gdpp_onsite_events',
    component: () => import('@/views/pages/support/GdppOnsiteEvents.vue'),
    meta: { label: '현장 이벤트 등록', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/support/:FAIR_ID/request-sponsor-prize',
    name: 'support-gdpp_request_sponsor_prize',
    component: () => import('@/views/pages/support/GdppRequestSponsorPrize.vue'),
    meta: { label: '협찬 경품 신청', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/brands',
    name: 'support-brand',
    component: () => import('@/views/pages/support/Brand.vue'),
    meta: { label: '공식 노출 회사명 등록', isRequireExhibitorId: true, isRequireApproval: false }
  },
  {
    path: '/exhibitor/:FAIR_ID/invitation',
    name: 'promotion-invitation',
    component: () => import('@/views/pages/promotion/Invitation.vue'),
    meta: { label: '무료 초청장 발송 시스템', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/press-release',
    name: 'promotion-press_release',
    component: () => import('@/views/pages/promotion/InterviewAndPressRelease.vue'),
    meta: { label: '인터뷰 및 보도자료 신청', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/banners',
    name: 'promotion-banner_list',
    component: () => import('@/views/pages/promotion/BannerList.vue'),
    meta: { label: '사업장 게시용 현수막 신청', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/online-promotion-comp',
    name: 'promotion-online-comp',
    component: () => import('@/views/pages/promotion/OnlinePromotionComp.vue'),
    meta: { label: '온라인 홍보 자료 등록', isRequireExhibitorId: true, isRequireApproval: false }
  },
  {
    path: '/exhibitor/:FAIR_ID/online-magazine',
    name: 'promotion-online_magazine',
    component: () => import('@/views/pages/promotion/OnlineMagazine.vue'),
    meta: { label: '전문지 광고비 지원(쿼터광고)', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/banner-downloads',
    name: 'promotion-banner_download',
    component: () => import('@/views/pages/promotion/BannerDownload.vue'),
    meta: { label: '전시 참가 홍보 이미지', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/mobile-invitation',
    name: 'promotion-mobile_invitation',
    component: () => import('@/views/pages/promotion/MobileInvitation.vue'),
    meta: { label: '참가기업 전용 모바일 초청장', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/payback-challenge',
    name: 'promotion-payback_challenge',
    component: () => import('@/views/pages/promotion/PaybackChallenge.vue'),
    meta: { label: '부스 참가비 페이백 챌린지', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/exhibitor/:FAIR_ID/buyer-invitation',
    name: 'promotion-buyer_invitation',
    component: () => import('@/views/pages/promotion/BuyerInvitation.vue'),
    meta: {
      label: '해외바이어 초청 지원 프로그램 신청',
      isRequireExhibitorId: true
    }
  },
  {
    path: '/coupon/:FAIR_ID/cobe-coupons',
    name: 'promotion-cobe_coupons',
    component: () => import('@/views/pages/promotion/CobeCoupons.vue'),
    meta: { label: '코베 쿠폰 및 약정 신청서', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/coupon/:FAIR_ID/settlement',
    name: 'promotion-cobe_settlement',
    component: () => import('@/views/pages/promotion/CobeSettlement.vue'),
    meta: { label: '코베 쿠폰 정산 확인', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/coupon/:FAIR_ID/self-coupons',
    name: 'promotion-cobe_self_coupons',
    component: () => import('@/views/pages/promotion/CobeSelfCoupons.vue'),
    meta: { label: '자체 쿠폰 신청서', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/gdpp/:FAIR_ID/promotion-kits',
    name: 'promotion-kits',
    component: () => import('@/views/pages/promotion/Kits.vue'),
    meta: { label: '홍보키트', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/bms-login',
    name: 'bms-login',
    component: () => import('@/views/pages/BmsLogin.vue'),
    meta: { label: '해외바이어 초청 수출상담회', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/notice/:FAIR_ID',
    name: 'etc-notice',
    component: () => import('@/views/pages/etc/Notice.vue'),
    meta: { label: '공지사항' }
  },
  {
    path: '/manual/:FAIR_ID',
    name: 'etc-manual_download',
    component: () => import('@/views/pages/etc/ManualDownload.vue'),
    meta: { label: '매뉴얼 다운로드', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/support/:FAIR_ID/construction-company',
    name: 'support-construction_company',
    component: () => import('@/views/pages/support/ConstructionCompany.vue'),
    meta: { label: '[독립부스 필수]시공사 등록', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/support/:FAIR_ID/cs-manager',
    name: 'support-cs_manager',
    component: () => import('@/views/pages/support/CsManager.vue'),
    meta: { label: 'CS 담당자 등록', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/notice/:FAIR_ID/bid-notice',
    name: 'etc-bid_notice',
    component: () => import('@/views/pages/etc/BidNotice.vue'),
    meta: { label: '지원사업 공고', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    // 해당 전시에 개설된 컨설팅관 메뉴가 동적으로 생성되게 처리되어 있음 - CommonController 내 menuItems() 참고
    path: '/consult/:FAIR_CONSULT_ID/visitors/:FAIR_ID',
    name: 'consult-visitors',
    component: () => import('@/views/pages/ConsultVisitorList.vue'),
    meta: { label: '컨설팅관', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/bms/:FAIR_ID/bms-register',
    name: 'bms-register',
    component: () => import('@/views/pages/bms/BmsRegister.vue'),
    meta: { label: '비즈매칭 등록', isRequireExhibitorId: true, isRequireApproval: true }
  },
  {
    path: '/replace-bms-exh',
    name: 'replace-bms-exh',
    component: () => import('@/views/pages/bms/ReplaceBmsExh.vue'),
    meta: { label: '비즈매칭 참가업체', isRequireExhibitorId: true, isRequireApproval: true }
  }
]

export const menuItemsRequireFairId = menuRoutes.filter((mr) => mr.path.includes(':FAIR_ID')).map((mr) => mr.name)

export function canDisplay(menuItem: MenuItem, exhibitor: Exhibitor): boolean {
  const route = menuRoutes.find((mr) => mr.name === menuItem.to)

  if (!route) {
    return false
  }

  const canDisplay = route.meta?.canDisplay

  if (!canDisplay) {
    return true
  }

  return canDisplay(exhibitor)
}

export function isRequireExhibitorId(menuItem: MenuItem): boolean {
  const menuRoute = menuRoutes.find((mr) => mr.name === menuItem.to)
  return menuRoute?.meta?.isRequireExhibitorId ?? false
}

export function isRequireApprovalExhibitor(menuItem: MenuItem): boolean {
  const menuRoute = menuRoutes.find((mr) => mr.name === menuItem.to)
  return menuRoute?.meta?.isRequireApproval ?? false
}

export function getMatchingParameterNames(routeName: string): string[] {
  const menuRoute = menuRoutes.find((mr) => mr.name === routeName)

  if (!menuRoute) {
    return []
  }

  const matchingParameterNames = menuRoute.path
    .split('/')
    .filter((p) => p.startsWith(':'))
    .map((p) => p.substring(1))

  return matchingParameterNames
}

export interface MenuRoute {
  name: string
  params: MenuParams
}

export interface MenuParams {
  COMPANY_FAIR_REQ_ID?: number
  FAIR_ID?: number
}
